

import React, { useState } from 'react';
import { File, ArrowLeft, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { analyzeTxtFile } from '../../services/apiService';
import Swal from 'sweetalert2';

const ImportData = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const navigate = useNavigate();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];

    if (
      file &&
      (
        file.type === 'text/plain' ||
        file.type === 'application/zip' ||
        file.type === 'application/x-zip-compressed'
      )
    ) {
      setSelectedFile(file);
      setUploadStatus('idle');
      setAnalysisResult(null);
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid File Type',
        text: 'Please select a valid TXT or ZIP file.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2563eb', // Tailwind blue-600
      });
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files[0];

    if (
      file &&
      (
        file.type === 'text/plain' ||
        file.type === 'application/zip' ||
        file.type === 'application/x-zip-compressed'
      )
    ) {
      setSelectedFile(file);
      setUploadStatus('idle');
      setAnalysisResult(null);
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid File',
        text: 'Please drop a valid TXT or ZIP file.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2563eb', // Tailwind blue-600
      });
    }
  };

  const handleDropZoneClick = () => {
    document.getElementById('file-upload').click();
  };

  const handleBackClick = () => {
    navigate('/audiobuzzManagement');
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      Swal.fire({
        icon: 'warning',
        title: 'File Required',
        text: 'Please select a file before starting the analysis.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2563eb', // Tailwind blue-600
      });
      return;
    }

    setIsAnalyzing(true);
    setUploadStatus('analyzing');

    const formData = new FormData();
    // Backend expects the field name as 'file'
    formData.append('file', selectedFile);

    try {
      console.log('Analyzing file:', selectedFile.name);

      const result = await analyzeTxtFile(formData);

      setAnalysisResult(result);
      setUploadStatus('success');

      Swal.fire({
        icon: 'success',
        title: 'Analysis Completed',
        text: 'The file has been analyzed successfully.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#16a34a', // green-600
      });

      console.log('Analysis completed successfully:', result);

    } catch (error) {
      setUploadStatus('error');
      console.error('Analysis error:', error);

      Swal.fire({
        icon: 'error',
        title: 'Analysis Failed',
        text: error?.message || 'An unexpected error occurred during analysis.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc2626', // red-600
      });

    } finally {
      setIsAnalyzing(false);
    }
  };

  // Helper function to get harmonic label
  const getHarmonicLabel = (harmonicNumber) => {
    if (harmonicNumber === 1) return 'Basic Frequency';
    const labels = ['Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth'];
    return `${labels[harmonicNumber - 2]} Harmonic`;
  };

  // Get all results from analysis
  const allResults = analysisResult?.results || [];
  const totalRecords = allResults.length;

  // Get current record
  const currentResult = allResults[currentIndex];
  const resultData = currentResult?.result;
  const harmonics = resultData?.harmonics || [];
  const firstFiveHarmonics = harmonics.slice(0, 5);
  const baseFrequency = resultData?.base_frequency || 'N/A';

  // Pagination handlers
  const handlePrevious = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleNext = () => {
    if (currentIndex < totalRecords - 1) setCurrentIndex(currentIndex + 1);
  };
  return (
    <div className="bg-black text-white min-h-screen p-4">
      <div className="flex items-center mb-6">
        <button className="text-white hover:text-gray-400 transition-colors mr-4">
          <ArrowLeft className="w-6 h-6" onClick={handleBackClick} />
        </button>
        <h1 className="flex-1 text-2xl sm:text-3xl font-bold text-center">
          Import Your Data
        </h1>
      </div>

      <div className="w-full max-w-7xl mx-auto">
        <p className="text-gray-400 text-center text-md mb-10">
          Select a TXT or ZIP file from your computer to begin analysis.
        </p>

        {/* ✅ UPLOAD SECTION - ALWAYS VISIBLE */}
        <div className="mb-10">
          <div

            className={`border-2 border-dashed border-gray-400 rounded-lg p-8 text-center transition-colors h-[220px] sm:h-[320px] md:h-[400px] flex flex-col justify-center items-center cursor-pointer ${isDragging ? 'border-blue-500 bg-blue-500/10' : ''} ${isAnalyzing ? 'pointer-events-none opacity-75' : ''}`}

            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={!isAnalyzing ? handleDropZoneClick : undefined}
          >
            {isAnalyzing ? (
              <div className="space-y-4">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <p className="text-gray-300 font-medium">Analyzing file...</p>
              </div>
            ) : (
              <>
                <div className="w-12 h-12 mx-auto mb-4 bg-gray-700 rounded-full flex items-center justify-center text-gray-400">
                  <File className="w-8 h-8" />
                </div>
                <p className="text-gray-400 text-md mb-2">
                  Drag & drop or click to select
                </p>
                <p className="text-md text-gray-500">TXT, ZIP</p>
              </>
            )}
          </div>

          {/* File Selection */}
          <div className="flex items-center justify-between mt-6 gap-4">
            <input
              type="file"
              id="file-upload"
              accept=".txt,.zip,text/plain,application/zip,application/x-zip-compressed"
              onChange={handleFileSelect}
              className="hidden"
            />
            <label
              htmlFor="file-upload"
              className="flex-1 bg-gray-800 rounded-lg px-4 sm:py-3 py-2 text-gray-400 cursor-pointer hover:bg-gray-700 transition-colors truncate"
            >
              {selectedFile ? selectedFile.name : 'No file selected'}
            </label>
          </div>

          {/* Upload Status Messages */}
          {uploadStatus === 'error' && (
            <div className="mt-4 p-4 bg-red-900/30 border border-red-500 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-400 font-medium">Analysis failed. Please try again.</p>
              </div>
            </div>
          )}

          {/* Analyze Button */}
          <button
            onClick={handleAnalyze}
            disabled={!selectedFile || isAnalyzing}
            className={`w-full mt-4 py-2 sm:py-3 text-base sm:text-xl rounded-lg font-semibold transition-colors ${selectedFile && !isAnalyzing
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
          >
            {isAnalyzing ? 'Analyzing...' : 'Analyze'}
          </button>

        </div>

        {/* ✅ SUCCESS MESSAGE - Show when analysis completes */}
        {uploadStatus === 'success' && analysisResult && (
          <div className="p-4 bg-green-900/30 border border-green-500 rounded-lg flex items-start gap-3 mb-10">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-400 sm:text-lg text-sm font-medium">Analysis completed successfully!</p>
              <p className="text-green-300 text-sm mt-1">{totalRecords} file(s) analyzed</p>
            </div>
          </div>
        )}

        {/* ✅ DETECTION RESULTS SECTION - Appears Below Upload */}
        {analysisResult && resultData && (
          <div className="bg-gray-900 rounded-lg shadow-lg p-6 mb-10 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-4 border-b border-gray-700 pb-4">Detection Results</h2>

            <p className="text-gray-300 mb-6 sm:text-lg text-sm leading-relaxed">
              1. The possible audio buzz may occur at the fundamental frequency of{' '}
              <span className="font-semibold">{baseFrequency} Hz</span>, as indicated by the spectrogram and harmonic
              record shown below.
            </p>

            {/* Grid Layout: Image + Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
              {/* Spectrogram Image */}
              <div
                className="bg-gray-800 rounded-lg flex justify-center items-center min-h-[100px] sm:min-h-[300px] lg:min-h-[400px] w-full cursor-pointer border border-gray-700"
                onClick={() => setIsPreviewOpen(true)}
              >
                {resultData.spectrum_graph_s3_url ? (
                  <img
                    src={resultData.spectrum_graph_s3_url}
                    alt={`Spectrogram showing audio buzz at ${baseFrequency} Hz`}
                    className="max-w-full max-h-full object-contain rounded"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML =
                        '<div class="text-gray-500">Image not available</div>';
                    }}
                  />
                ) : (
                  <div className="text-gray-500">No spectrum image available</div>
                )}
              </div>

              {/* Tables Section */}
              <div className="space-y-6">
                {/* Harmonics Table */}
                <div className="overflow-x-hidden">
                  <table className="w-full table-fixed bg-gray-800 border border-gray-600 rounded-lg">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-2 py-2 sm:px-4 sm:py-4 text-left text-xs sm:text-sm font-medium text-white uppercase tracking-wider border-b border-r border-gray-600" />
                        <th className="px-2 py-2 sm:px-4 sm:py-4 text-left text-xs sm:text-sm font-medium text-white uppercase tracking-wider border-b border-r border-gray-600">
                          Frequency (Hz)
                        </th>
                        <th className="px-2 py-2 sm:px-4 sm:py-4 text-left text-xs sm:text-sm font-medium text-white uppercase tracking-wider border-b border-gray-600">
                          Energy (dB)
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-x divide-gray-600">
                      {firstFiveHarmonics.length > 0 ? (
                        firstFiveHarmonics.map((harmonic, idx) => (
                          <tr key={harmonic._id || idx} className="bg-gray-800">
                            <td className="px-2 py-2 sm:px-4 sm:py-4 text-xs sm:text-sm text-white border-r border-gray-600 whitespace-normal break-words">
                              {getHarmonicLabel(harmonic.harmonic_number)}
                            </td>
                            <td className="px-2 py-2 sm:px-4 sm:py-4 text-xs sm:text-sm text-white border-r border-gray-600">
                              {Math.round(harmonic.frequency)}
                            </td>
                            <td className="px-2 py-2 sm:px-4 sm:py-4 text-xs sm:text-sm text-white">
                              {harmonic.db_level.toFixed(0)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-2 py-2 sm:px-4 sm:py-4 text-xs sm:text-sm text-gray-400 text-center"
                          >
                            No harmonic data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Statistics Table */}
                <div className="overflow-x-hidden">
                  <table className="w-full table-fixed bg-gray-800 border border-gray-600 rounded-lg">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-2 py-2 sm:px-4 sm:py-3 text-left text-xs sm:text-sm font-medium text-white uppercase tracking-wider border-b border-r border-gray-600" />
                        <th className="px-2 py-2 sm:px-4 sm:py-3 text-left text-xs sm:text-sm font-medium text-white uppercase tracking-wider border-b border-gray-600">
                          Energy (dB)
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-x divide-gray-600">
                      <tr className="bg-gray-800">
                        <td className="px-2 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm font-medium text-white border-r border-gray-600 whitespace-normal break-words">
                          AVG (excluding basic frequency)
                        </td>
                        <td className="px-2 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm font-medium text-white">
                          {resultData.mean !== null && resultData.mean !== undefined
                            ? resultData.mean.toFixed(0)
                            : 'N/A'}
                        </td>
                      </tr>

                      <tr className="bg-gray-800">
                        <td className="px-2 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm font-medium text-white border-r border-gray-600">
                          Standard Deviation
                        </td>
                        <td className="px-2 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm font-medium text-white">
                          {resultData.std !== null && resultData.std !== undefined
                            ? resultData.std.toFixed(0)
                            : 'N/A'}
                        </td>
                      </tr>

                      <tr className="bg-gray-800">
                        <td className="px-2 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm font-medium text-white border-r border-gray-600">
                          AVG Standard Deviation
                        </td>
                        <td className="px-2 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm font-medium text-white">
                          {resultData.mean !== null && resultData.std !== null
                            ? (resultData.mean + resultData.std).toFixed(0)
                            : 'N/A'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Remark */}
                <p className="text-sm text-gray-400">
                  <span className="font-semibold">Remark:</span> Calculation of both the average and standard deviation
                  excludes the fundamental frequency.
                </p>
              </div>
            </div>

            {/* ✅ PAGINATION - Like AudioSpectrogram */}
            {totalRecords > 1 && (
              <div
                className="fixed bottom-0 left-0 right-0 w-full py-4 bg-black z-50 px-4 md:px-6 flex justify-center items-center gap-2 text-xs md:text-sm text-gray-400 border-t border-gray-500"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
              >
                <button
                  className=" text-gray-100 px-3 py-2 rounded hover:bg-gray-700 transition-colors border border-gray-100 disabled:opacity-50 disabled:cursor-not-allowed mb-1 -mt-2"
                  disabled={currentIndex === 0}
                  onClick={handlePrevious}
                  aria-label="Previous"
                >
                  &lt;
                </button>

                <span className="min-w-[55px] text-center mb-1 text-gray-100 text-base -mt-2 ">
                  {currentIndex + 1} / {totalRecords}
                </span>

                <button
                  className="text-gray-100 px-3 py-2 rounded hover:bg-gray-700 transition-colors border border-gray-100 disabled:opacity-50 disabled:cursor-not-allowed mb-1 -mt-2"
                  disabled={currentIndex === totalRecords - 1}
                  onClick={handleNext}
                  aria-label="Next"
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isPreviewOpen && resultData?.spectrum_graph_s3_url && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsPreviewOpen(false);
          }}
        >
          <div className="relative">

            {/* Close button – SAME responsive positioning */}
            <button
              onClick={() => setIsPreviewOpen(false)}
              className="
          absolute -top-10 -right-1
          sm:-top-14 sm:-right-5
          bg-gray-800 hover:bg-gray-700
          text-white rounded-full
          sm:w-9 sm:h-9 w-7 h-7 sm:w-10 sm:h-10
          flex items-center justify-center
          text-2xl font-bold z-20
        "
            >
              ×
            </button>

            {/* Preview container */}
            <div className="bg-gray-900 max-w-6xl max-h-[98vh] overflow-auto relative flex items-center justify-center">

              <img
                src={resultData.spectrum_graph_s3_url}
                alt={`Spectrogram showing audio buzz at ${baseFrequency} Hz`}
                className="
            w-full max-w-full
            h-auto
            max-h-[95vh]
            sm:max-h-[90vh]
            lg:max-h-[98vh]
            object-contain
          "
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ImportData;
